import { Box, BoxProps, Grid as CGrid, Center, Container, Flex, Heading } from '@chakra-ui/react';
import { faArrowRight } from '@fortawesome/pro-solid-svg-icons';
import { CMS } from '@waitroom/models';
import * as React from 'react';
import CmsButton from '../../../../cms/components/Button/CmsButton';
import { secondaryTextColor } from '../../../../cms/utils/base';
import { isLinkDefined } from '../../../../cms/utils/sanity';
import Carousel from '../../Carousel/Carousel';
import { Icon } from '../../Icon/Icon';
import Markdown from '../../Markdown/Markdown';
import GridItem from './GridItem/GridItem';

const Grid = ({
  heading,
  content,
  button,
  link,
  footnote,
  items,
  align,
  slider,
  colored,
  ...rest
}: CMS.Grid & BoxProps): React.ReactElement | null => {
  const len = items?.length;
  const colLen = Math.min(len || 0, 4);
  const hasButton = isLinkDefined(button);
  const hasLink = isLinkDefined(link);
  const itemsUi = React.useMemo(
    () =>
      items?.map((item, i) => (
        <GridItem
          key={i}
          item={item}
          align={align}
          colored={colored}
          index={i}
          w={slider ? ['250px', '280px'] : undefined}
          py={6}
        />
      )),
    [align, colored, items, slider],
  );

  return (
    <Box {...rest}>
      <Container maxW={slider ? '' : 'container.2xl'}>
        {heading || content ? (
          <Flex
            w="full"
            flexDirection="column"
            justify="center"
            textAlign="center"
            rowGap={8}
            mb={12}
            maxW="1000px"
            mx="auto"
          >
            {heading && (
              <Heading as="h2" size="5xl" lineHeight="shorter">
                {heading}
              </Heading>
            )}
            {content && <Markdown fontSize="xl">{content}</Markdown>}
          </Flex>
        ) : null}
        {slider ? (
          <Box w="full">
            <Carousel
              display="flex"
              alignItems="center"
              justifyContent="center"
              interval={4000}
              loop
            >
              <Carousel.Items columnGap={[8, 10]} mx={-4}>
                {itemsUi || []}
              </Carousel.Items>
            </Carousel>
          </Box>
        ) : (
          <CGrid
            templateColumns={{
              base: 'repeat(1, 1fr)',
              sm: `repeat(${Math.ceil(colLen / 4)}, 1fr)`,
              md: `repeat(${Math.ceil(colLen / 3)}, 1fr)`,
              lg: `repeat(${Math.ceil(colLen / 2)}, 1fr)`,
              xl: `repeat(${colLen}, 1fr)`,
            }}
            alignItems="flex-start"
            justifyContent={{ base: 'center', md: 'flex-start' }}
            columnGap={colored ? [6, 6, 8, 10, 12] : 0}
            rowGap={colored ? [12] : 4}
            mx={-4}
          >
            {itemsUi}
          </CGrid>
        )}
        {hasButton || hasLink ? (
          <Center flexWrap="wrap" alignItems="center" mt={12}>
            {hasButton && (
              <CmsButton
                data={button}
                isButton
                size={['def', 'md', 'lg']}
                minW={['180px', '180px']}
              >
                {button.text}
              </CmsButton>
            )}
            {hasLink && (
              <CmsButton
                data={link}
                size={['sm', 'md', 'lg']}
                fontSize={['sm', 'md', 'lg']}
                px={[4, 6]}
                rightIcon={<Icon icon={faArrowRight} />}
                minW={['180px', '180px']}
              >
                {link.text}
              </CmsButton>
            )}
          </Center>
        ) : null}
        {footnote && (
          <Markdown textAlign="center" color={secondaryTextColor(rest.bgColor)} mt={4}>
            {footnote}
          </Markdown>
        )}
      </Container>
    </Box>
  );
};
export default Grid;
